import { linkifyKingAccountNumbersInMarkdown } from './accountLinks'
import { linkifyKnownCuidsInMarkdown, type KnownCuidMap } from './cuidExpand'

const DEFAULT_JIRA_BASE = 'https://kingenergy.atlassian.net'

/** Matches keys like WEB-1234, BUS-9 (not mid-word). */
export const JIRA_KEY_RE = /\b([A-Z][A-Z0-9]{1,10}-\d+)\b/g

export function jiraBrowseUrl(issueKey: string, baseUrl = DEFAULT_JIRA_BASE): string {
  const key = issueKey.trim().toUpperCase()
  return `${baseUrl.replace(/\/$/, '')}/browse/${key}`
}

export function extractJiraKeyFromHref(href: string | undefined | null): string | null {
  if (!href) return null
  try {
    const url = new URL(href, DEFAULT_JIRA_BASE)
    const browse = url.pathname.match(/\/browse\/([A-Z][A-Z0-9]+-\d+)/i)
    if (browse?.[1]) return browse[1].toUpperCase()
    const selected = url.searchParams.get('selectedIssue')
    if (selected && /^[A-Z][A-Z0-9]+-\d+$/i.test(selected)) return selected.toUpperCase()
  } catch {
    // ignore
  }
  return null
}

/**
 * Turn bare Jira keys into markdown links. Skips fenced/inline code and existing markdown links.
 */
export function linkifyJiraKeysInMarkdown(markdown: string, baseUrl = DEFAULT_JIRA_BASE): string {
  const placeholders: string[] = []
  const protect = (chunk: string) => {
    const token = `\u0000JIRA${placeholders.length}\u0000`
    placeholders.push(chunk)
    return token
  }

  let text = markdown
    // fenced code
    .replace(/```[\s\S]*?```/g, protect)
    // inline code
    .replace(/`[^`\n]+`/g, protect)
    // existing markdown links
    .replace(/\[[^\]]*]\([^)]+\)/g, protect)

  text = text.replace(JIRA_KEY_RE, (key) => `[${key}](${jiraBrowseUrl(key, baseUrl)})`)

  return text.replace(/\u0000JIRA(\d+)\u0000/g, (_, idx) => placeholders[Number(idx)] ?? '')
}

/**
 * Autolink bare http(s) URLs that are not already markdown links.
 */
export function linkifyBareUrlsInMarkdown(markdown: string): string {
  const placeholders: string[] = []
  const protect = (chunk: string) => {
    const token = `\u0000URL${placeholders.length}\u0000`
    placeholders.push(chunk)
    return token
  }

  let text = markdown
    .replace(/```[\s\S]*?```/g, protect)
    .replace(/`[^`\n]+`/g, protect)
    .replace(/\[[^\]]*]\([^)]+\)/g, protect)

  text = text.replace(/(https?:\/\/[^\s<>()[\]]+)/g, (url) => {
    const cleaned = url.replace(/[),.;:!?]+$/u, '')
    const trailing = url.slice(cleaned.length)
    return `[${cleaned}](${cleaned})${trailing}`
  })

  return text.replace(/\u0000URL(\d+)\u0000/g, (_, idx) => placeholders[Number(idx)] ?? '')
}

export type PrepareChatMarkdownOptions = {
  /** When set, bare king account numbers deep-link to this tenant's account panel. */
  tenantId?: string
  /** When set, known admin CUIDs deep-link / expand to entity names. */
  knownCuids?: KnownCuidMap
}

export function prepareChatMarkdown(
  markdown: string,
  options: PrepareChatMarkdownOptions = {}
): string {
  let text = linkifyBareUrlsInMarkdown(markdown)
  text = linkifyJiraKeysInMarkdown(text)
  if (options.tenantId) {
    text = linkifyKingAccountNumbersInMarkdown(text, options.tenantId)
  }
  if (options.knownCuids && Object.keys(options.knownCuids).length > 0) {
    text = linkifyKnownCuidsInMarkdown(text, options.knownCuids)
  }
  return text
}
